import { useLocalSearchParams } from 'expo-router';
import { type ComponentProps, useCallback, useMemo } from 'react';
import { PixelRatio, StyleSheet, View } from 'react-native';

import { AnchorSectionList, MqSwitch, XStack, createMqStyles } from '@fhs/ui';
import { SectionListHeader } from '@fhs/ui/src/components/anchor-section-list/section-list-header';
import { ScrollRestorationKey } from '@fhs/utils';

import { useMenuClickEvents } from '../analytics';
import { MenuListLegalText } from '../components/menu-list-legal-text';
import { MenuListItemRow } from '../components/menu-list-rows';
import { MenuOffersAndRewards } from '../components/menu-offers-and-rewards';
import { MenuOffersLink } from '../components/menu-offers-link';
import type { ScreenMenuListProps } from '../types';

export function ScreenMenuList(props: ScreenMenuListProps) {
  const mqStyles = useMqStyles();
  const { logMenuItemClick } = useMenuClickEvents();
  const mobileRenderItem = useCallback<
    NonNullable<ComponentProps<typeof AnchorSectionList.SectionList>['renderItem']>
  >(
    ({ item: datum }) => {
      switch (datum.type) {
        case 'section':
          return (
            <SectionListHeader
              nativeID={datum.section.id}
              title={datum.section.title}
              {...{
                dataSet: {
                  fhsSectionHeader: datum.section.id,
                  ...(!datum.section.title ? { fhsEmptySectionHeader: 'true' } : {}),
                },
              }}
              style={datum.section.title ? styles.mobileSectionHeader : undefined}
            />
          );
        case 'custom':
          return (
            <View {...{ dataSet: { fhsSectionContent: datum.section.id } }}>
              <MenuOffersAndRewards />
            </View>
          );
        case 'item': {
          const section = props.sections.find(candidate => candidate.slug === datum.parentSectionId);
          const item = section?.items[datum.indexWithinSection];
          return item ? (
            <View style={styles.mobileItem}>
              <MenuListItemRow
                item={item}
                index={datum.index}
                href={item.url}
                onPress={selectedItem => logMenuItemClick(selectedItem.displayName)}
              />
            </View>
          ) : null;
        }
      }
    },
    [props.sections, logMenuItemClick]
  );

  const sections: ComponentProps<typeof AnchorSectionList>['sections'] = useMemo(
    () => [
      {
        id: 'offers-and-rewards',
        title: '',
        anchorTitle: 'Offers & Rewards',
        image: {
          asset: require('../assets/img/offers-and-rewards-icon.webp'),
          altText: 'Offers & Rewards',
        },
        items: <MenuOffersLink style={mqStyles.offersLink} />,
      },
      ...props.sections.map(rawSection => ({
        id: rawSection.slug,
        title: rawSection.displayName,
        anchorTitle: rawSection.displayName,
        image: {
          asset: {
            uri: rawSection.image?.asset?.uri,
            blurHash: rawSection.image?.asset?.blurHash ?? undefined,
          },
          altText: rawSection.image?.altText || rawSection.displayName || '',
        },
        items: rawSection.items.map(rawItem => {
          const baseUri = rawItem.image?.asset?.uri;
          let sizedUri: string | undefined;
          if (baseUri) {
            try {
              const imageUrl = new URL(baseUri);
              imageUrl.searchParams.set('h', '250');
              imageUrl.searchParams.set(
                'dpr',
                Math.min(Math.max(PixelRatio.get(), 1.75), 2.5).toString()
              );
              imageUrl.searchParams.set('fm', 'webp');
              sizedUri = imageUrl.toString();
            } catch {
              sizedUri = baseUri;
            }
          }

          return {
            id: rawItem.slug,
            href: rawItem.url,
            title: rawItem.displayName,
            description: rawItem.descriptionShort,
            badge: rawItem.badge ?? undefined,
            image: {
              asset: {
                uri: sizedUri,
                blurHash: rawItem.image?.asset?.blurHash ?? undefined,
              },
              altText: rawItem.image?.altText || rawItem.displayName,
            },
            isAvailable: rawItem.isAvailable,
          };
        }),
      })),
    ],
    [mqStyles, props.sections]
  );

  const { section: defaultSectionId } = useLocalSearchParams<{ section: string }>();

  return (
    <AnchorSectionList
      sections={sections}
      defaultSectionId={defaultSectionId}
      scrollRestorationKey={ScrollRestorationKey.MenuList}
    >
      <MqSwitch
        $base={() => (
          <View style={styles.container}>
            <AnchorSectionList.AnchorListHorizontal titleTextProps={{ size: 'md' }} />
            <AnchorSectionList.SectionList
              numColumns={1}
              renderItem={mobileRenderItem}
              footer={<MenuListLegalText />}
            />
          </View>
        )}
        $gteDesktop={() => (
          <View style={styles.container}>
            <XStack style={styles.xStack}>
              <AnchorSectionList.AnchorListVertical />
              <AnchorSectionList.SectionList footer={<MenuListLegalText />} />
            </XStack>
          </View>
        )}
      />
    </AnchorSectionList>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    maxHeight: '100%',
  },
  xStack: {
    height: '100%',
  },
  mobileSectionHeader: {
    marginTop: 0,
    paddingVertical: 24,
  },
  mobileItem: {
    paddingBottom: 16,
  },
});

const useMqStyles = createMqStyles({
  offersLink: {
    $base: {
      marginBottom: 0,
      overflow: 'hidden',
    },
    $gteDesktop: {
      marginBottom: 0,
      marginTop: 4,
    },
  },
});
